import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, type Track, type TrackState } from '../api.js';
import StateBadge from '../components/StateBadge.js';
import RepoBadge from '../components/RepoBadge.js';

type DotColor = 'green' | 'yellow' | 'red' | 'gray';

const DOT_FOR_STATE: Record<TrackState, DotColor> = {
  DRAFTING_SPEC: 'gray',
  SPEC_REVIEW: 'yellow',
  AWAITING_PREREQS: 'yellow',
  QUEUED: 'yellow',
  BUILDING: 'green',
  VERIFYING: 'green',
  PR_OPEN: 'green',
  MERGED: 'green',
  DONE: 'green',
  BLOCKED_HUMAN: 'red',
  FAILED_BUILD: 'red',
  FAILED_SCOPE: 'red',
  CANCELLED: 'gray',
};

const IN_FLIGHT: TrackState[] = ['BUILDING', 'VERIFYING', 'PR_OPEN'];
const NEEDS_ATTENTION: TrackState[] = [
  'BLOCKED_HUMAN',
  'FAILED_BUILD',
  'FAILED_SCOPE',
  'AWAITING_PREREQS',
];
const QUEUED_STATES: TrackState[] = ['QUEUED'];
const RECENTLY_DONE_STATES: TrackState[] = ['MERGED', 'DONE'];

const COLORS: Record<DotColor, string> = {
  green: 'var(--green)',
  yellow: 'var(--yellow)',
  red: 'var(--red)',
  gray: 'var(--gray)',
};

function relativeTime(iso: string): string {
  const then = new Date(iso).getTime();
  const now = Date.now();
  const diff = Math.max(0, now - then);
  const s = Math.floor(diff / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

function StatusDot({ color }: { color: DotColor }) {
  return (
    <span
      aria-hidden
      style={{
        display: 'inline-block',
        width: 14,
        height: 14,
        borderRadius: '50%',
        background: COLORS[color],
        flexShrink: 0,
      }}
    />
  );
}

function TrackRow({ track }: { track: Track }) {
  const dot = DOT_FOR_STATE[track.state] ?? 'gray';
  return (
    <Link
      to={`/tracks/${track.id}`}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 14,
        padding: '18px 16px',
        background: 'var(--surface)',
        border: '1px solid var(--border)',
        borderRadius: 8,
        marginBottom: 10,
        textDecoration: 'none',
        color: 'inherit',
        minHeight: 64,
      }}
    >
      <StatusDot color={dot} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            fontWeight: 600,
            fontSize: 15,
            marginBottom: 4,
          }}
        >
          <RepoBadge targetRepo={track.spec.target_repo} />
          <span>{track.title}</span>
        </div>
        <div
          style={{
            display: 'flex',
            gap: 10,
            alignItems: 'center',
            flexWrap: 'wrap',
          }}
        >
          <StateBadge state={track.state} />
          <span style={{ color: 'var(--muted)', fontSize: 12.5 }}>
            {relativeTime(track.updated_at)}
          </span>
          {track.blocked_by.length > 0 && (
            <span className="badge badge-yellow">
              blocked × {track.blocked_by.length}
            </span>
          )}
        </div>
      </div>
    </Link>
  );
}

function Section({
  title,
  color,
  tracks,
  emptyText,
}: {
  title: string;
  color: DotColor;
  tracks: Track[];
  emptyText: string;
}) {
  return (
    <div style={{ marginBottom: 24 }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          fontSize: 12,
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
          color: 'var(--muted)',
          fontWeight: 600,
          marginBottom: 10,
        }}
      >
        <StatusDot color={color} />
        <span>
          {title} ({tracks.length})
        </span>
      </div>
      {tracks.length === 0 ? (
        <div className="empty" style={{ padding: 12 }}>
          {emptyText}
        </div>
      ) : (
        tracks.map((t) => <TrackRow key={t.id} track={t} />)
      )}
    </div>
  );
}

function isDoneToday(track: Track): boolean {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const upd = new Date(track.updated_at).getTime();
  return upd >= today.getTime();
}

export default function Dashboard() {
  const [tracks, setTracks] = useState<Track[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const r = await api.listTracks();
        if (!cancelled) {
          setTracks(r.tracks);
          setError(null);
        }
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    const interval = setInterval(load, 15_000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, []);

  if (loading) return <div className="empty">Loading…</div>;
  if (error) return <div className="errors">{error}</div>;

  const inFlight = tracks.filter((t) => IN_FLIGHT.includes(t.state));
  const needsAttention = tracks.filter((t) => NEEDS_ATTENTION.includes(t.state));
  const queued = tracks.filter((t) => QUEUED_STATES.includes(t.state));
  const recentlyDone = tracks
    .filter((t) => RECENTLY_DONE_STATES.includes(t.state))
    .sort(
      (a, b) =>
        new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime()
    )
    .slice(0, 10);

  const numBuilding = tracks.filter((t) => t.state === 'BUILDING').length;
  const numQueued = queued.length;
  const numNeedAttention = needsAttention.length;
  const numDoneToday = tracks.filter(
    (t) => RECENTLY_DONE_STATES.includes(t.state) && isDoneToday(t)
  ).length;
  const hasRed = tracks.some(
    (t) => DOT_FOR_STATE[t.state] === 'red'
  );

  return (
    <div>
      {hasRed && (
        <div
          style={{
            background: 'var(--red-bg)',
            color: 'var(--red)',
            border: '1px solid #fecaca',
            padding: '14px 16px',
            borderRadius: 8,
            marginBottom: 16,
            fontWeight: 600,
          }}
        >
          Attention needed — {numNeedAttention} track
          {numNeedAttention === 1 ? '' : 's'} in a red state.
        </div>
      )}

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
          gap: 12,
          marginBottom: 24,
        }}
      >
        <SummaryTile label="Building" value={numBuilding} color="green" />
        <SummaryTile label="Queued" value={numQueued} color="yellow" />
        <SummaryTile
          label="Need attention"
          value={numNeedAttention}
          color="red"
        />
        <SummaryTile label="Done today" value={numDoneToday} color="green" />
      </div>

      <Section
        title="In flight"
        color="green"
        tracks={inFlight}
        emptyText="Nothing building right now."
      />
      <Section
        title="Needs attention"
        color="red"
        tracks={needsAttention}
        emptyText="All clear."
      />
      <Section
        title="Queued"
        color="yellow"
        tracks={queued}
        emptyText="No queued tracks."
      />
      <Section
        title="Recently done"
        color="green"
        tracks={recentlyDone}
        emptyText="No completions yet."
      />
    </div>
  );
}

function SummaryTile({
  label,
  value,
  color,
}: {
  label: string;
  value: number;
  color: DotColor;
}) {
  return (
    <div
      style={{
        background: 'var(--surface)',
        border: '1px solid var(--border)',
        borderRadius: 8,
        padding: 14,
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          color: 'var(--muted)',
          fontSize: 11,
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
          fontWeight: 600,
        }}
      >
        <StatusDot color={color} />
        {label}
      </div>
      <div style={{ fontSize: 26, fontWeight: 700 }}>{value}</div>
    </div>
  );
}
